/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AssignmentModel,
  AssignmentRequestModel,
  AssignmentRequestReasonEnum,
  AssignmentViewModel,
  AvailabilityDto,
  CaseTypeEnum,
} from 'api/model';
import useModalStack from 'contexts/modal/useModalStack';
import { useCallback, useMemo } from 'react';
import useCompanies from 'contexts/basicData/useCompanies';
import {
  useAssignmentRequestsCreateAssignmentRequest,
  useAssignmentRequestsGetClosestCompany,
  useAssignmentRequestsGetClosestCompanyToZipCode,
} from 'api/assignment-requests/assignment-requests';
import toast from 'react-hot-toast';
import CreateAssignmentRequestModal from './CreateAssignmentRequestModal';
import TextButton from 'components/inputs/TextButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useCompanyGetClosestCompanyByUserId } from 'api/company/company';
import AddressAndTimePill from 'components/Address/AddressAndTimePill';
import { DayDrivingTime } from '../useDayDrivingTimes';

const FIXED_PADDING_TIME_IN_MILLISECONDS = 0; // removed padding time for now, as we want to plan back to back assignments now

const MyTextButton = styled(TextButton)`
  padding: 0px;
`;

type Props = {
  bookedAssignments?: AssignmentViewModel[];
  assignmentRequests?: AssignmentRequestModel[];
  dayDrivingTimes?: DayDrivingTime[];
  availability: AvailabilityDto;
  userId: number;
};

export default function useCreateAssignmentRequests({
  bookedAssignments = [],
  assignmentRequests = [],
  dayDrivingTimes = [],
  availability,
  userId,
}: Props) {
  const modalStack = useModalStack();
  const { companies } = useCompanies();

  const getClosestCompany = useAssignmentRequestsGetClosestCompany();
  const getClosestCompanyByUserId = useCompanyGetClosestCompanyByUserId();
  const getClosestCompanyByZip =
    useAssignmentRequestsGetClosestCompanyToZipCode();
  const firstLogisticsCompany = companies.find(
    (c) => c.isLogisticsCenter && c.name === 'Logistik - Strängnäs'
  ); // lock to strängnäs logistik for now, until we open up both locations

  // fetch the closest company address when the modal is opened and / or the assignment changes
  const fetchClosestCompanyAddress = async (
    assignment: AssignmentModel,
    caseType: CaseTypeEnum
  ) => {
    try {
      const result = await getClosestCompany.mutateAsync({
        params: {
          caseType,
          FromZip: assignment.fromZip,
          ToZip: assignment.toZip,
        },
      });
      return result;
    } catch (error) {
      // console.log(error);
      return null;
    }
  };

  const fetchClosestCompanyAddressToZipCode = async (zip: string) => {
    try {
      const result = await getClosestCompanyByZip.mutateAsync({
        params: {
          Zip: zip,
        },
      });
      return result;
    } catch (error) {
      // console.log(error);
      return null;
    }
  };

  const fetchClosestCompanyAddressToUserZipCode = async (_userId: number) => {
    try {
      const result = await getClosestCompanyByUserId.mutateAsync({
        params: {
          userId: _userId,
        },
      });
      return result;
    } catch (error) {
      // console.log(error);
      return null;
    }
  };

  const getFixedTimeBetweenLogisticsAndCompany = useCallback(
    (toCompanyId: number | null): number | undefined => {
      if (firstLogisticsCompany && toCompanyId !== null) {
        // find fixed time between the selected companies
        const fixedTimeBetweenCompanies =
          firstLogisticsCompany?.pricesFromHere.find(
            (c) => c.toCompanyId === toCompanyId
          )?.fixedTimeInMilliseconds ?? 0;

        return fixedTimeBetweenCompanies;
      }
      return undefined;
    },
    [firstLogisticsCompany]
  );

  const showCreateAssignmentRequestModal = ({
    reasonInfo,
    initialReason,
    earliestPossibleStartTime,
    lastPossibleArrivalTime,
    toCompanyId,
    onBehalfOfAssignment,
  }: {
    reasonInfo: React.ReactNode;
    initialReason: AssignmentRequestReasonEnum;
    earliestPossibleStartTime?: Date;
    lastPossibleArrivalTime?: Date;
    toCompanyId?: number;
    onBehalfOfAssignment?: AssignmentModel;
  }) => {
    modalStack.push(
      <CreateAssignmentRequestModal
        reasonInfo={reasonInfo}
        initialReason={initialReason}
        initialToCompanyId={toCompanyId}
        earliestPossibleStartTime={earliestPossibleStartTime}
        lastPossibleArrivalTime={lastPossibleArrivalTime}
        onBehalfOfAssignment={onBehalfOfAssignment}
        onChange={(newValue) => {
          // setAssignmentRequest(newValue);
        }}
        onCancel={() => {
          modalStack.pop();
        }}
        onConfirm={() => {
          modalStack.pop();
        }}
        forUserId={userId}
      />
    );
  };

  const handleCreateMorningAssignmentRequest = async () => {
    if (bookedAssignments.length === 0) {
      return;
    }

    // get the first booked assignment
    const firstBookedAssignment = dayDrivingTimes[0];
    if (!firstBookedAssignment || !firstBookedAssignment.assignment) {
      return;
    }

    const closestCompanyAddressId = await fetchClosestCompanyAddress(
      firstBookedAssignment.assignment.assignment,
      firstBookedAssignment.assignment.case.caseTypeID
    );

    const timeBetweenLogisticsAndClosestCompany =
      getFixedTimeBetweenLogisticsAndCompany(closestCompanyAddressId) ?? 0;

    if (!firstBookedAssignment.start) {
      toast.error(
        'Det går inte att skapa en annonsförfrågan för ett uppdrag som inte har en estimerad starttid.'
      );
      return;
    }

    // (was `!x === undefined`, which is always false — the guard never fired)
    if (!firstBookedAssignment.assignment.assignment.assignedTo) {
      toast.error(
        'Det går inte att skapa en annonsförfrågan för ett uppdrag som inte har en tilldelad användare.'
      );
      return;
    }

    const lastPossibleArrivalTime = new Date(
      ((firstBookedAssignment.start as Date)?.getTime() ?? 0) -
        FIXED_PADDING_TIME_IN_MILLISECONDS
    );

    showCreateAssignmentRequestModal({
      reasonInfo: (
        <div style={{ whiteSpace: 'wrap', lineHeight: '25px' }}>
          För att ta sig till dagens första uppdrag (
          <MyTextButton
            onClick={() => {
              window.open(
                `/sok/${firstBookedAssignment.assignment?.case.caseID}`,
                firstBookedAssignment.assignment?.case.caseID.toString()
              );
            }}
          >
            <span>
              {firstBookedAssignment.assignment?.case.registrationNumber}
            </span>{' '}
            <FontAwesomeIcon icon={faExternalLink} />
          </MyTextButton>
          <p>
            <AddressAndTimePill
              address={firstBookedAssignment.fromAddress}
              time={firstBookedAssignment.start}
            />
          </p>
        </div>
      ),
      initialReason: AssignmentRequestReasonEnum.StartOfDay,
      lastPossibleArrivalTime,
      toCompanyId: closestCompanyAddressId ?? undefined,
      onBehalfOfAssignment: firstBookedAssignment.assignment.assignment,
    });
  };

  const handleCreateAfternoonAssignmentRequest = async () => {
    const closestCompanyAddressId =
      await fetchClosestCompanyAddressToUserZipCode(userId);

    const lastAssignment = dayDrivingTimes[dayDrivingTimes.length - 1];
    if (!lastAssignment || !lastAssignment.assignment) {
      return;
    }

    const earliestPossibleStartTime = new Date(
      (lastAssignment.end?.getTime() ?? 0) + FIXED_PADDING_TIME_IN_MILLISECONDS
    );

    showCreateAssignmentRequestModal({
      reasonInfo: (
        <div>
          För att ta sig hem från dagens sista uppdrag (
          <MyTextButton
            onClick={() => {
              window.open(
                `/sok/${lastAssignment.assignment?.case.caseID}`,
                lastAssignment.assignment?.case.caseID.toString()
              );
            }}
          >
            <span>{lastAssignment.assignment?.case.registrationNumber}</span>{' '}
            <FontAwesomeIcon icon={faExternalLink} />
          </MyTextButton>
          <p>
            <AddressAndTimePill
              address={lastAssignment.toAddress}
              time={lastAssignment.start}
            />
          </p>
        </div>
      ),
      initialReason: AssignmentRequestReasonEnum.EndOfDay,
      earliestPossibleStartTime,
      toCompanyId: closestCompanyAddressId ?? undefined,
    });
  };

  const handleCreateMiddayAssignmentRequest = async (index: number) => {
    const currentAssignment = dayDrivingTimes[index - 1];
    // fetch closest company address to next assignment
    const nextAssignment = dayDrivingTimes[index];
    if (!nextAssignment || !nextAssignment.assignment || !currentAssignment) {
      toast.error('Det går inte att skapa en annonsförfrågan.');
      return;
    }

    const closestCompanyAddressId = await fetchClosestCompanyAddressToZipCode(
      nextAssignment.fromAddress.zip
    );

    const earliestPossibleStartTime = new Date(
      currentAssignment.end?.getTime() ?? 0
    );

    showCreateAssignmentRequestModal({
      reasonInfo: (
        <div style={{ whiteSpace: 'wrap', lineHeight: '25px' }}>
          Behöver ansluta{' '}
          {currentAssignment && (
            <p>
              från {currentAssignment.assignment?.case.registrationNumber} som
              slutar på{' '}
              <AddressAndTimePill
                address={currentAssignment.toAddress}
                time={currentAssignment.end}
              />
            </p>
          )}
          {nextAssignment && (
            <p>
              till {nextAssignment?.assignment?.case.registrationNumber} som
              börjar på{' '}
              <AddressAndTimePill
                address={nextAssignment.fromAddress}
                time={nextAssignment.start}
              />
            </p>
          )}
        </div>
      ),
      // Tillresa till nästa uppdrag räknas som arbetsdagens början.
      initialReason: AssignmentRequestReasonEnum.StartOfDay,
      lastPossibleArrivalTime: nextAssignment.start,
      earliestPossibleStartTime,
      toCompanyId: closestCompanyAddressId ?? undefined,
    });
  };

  return {
    handleCreateMorningAssignmentRequest,
    handleCreateAfternoonAssignmentRequest,
    handleCreateMiddayAssignmentRequest,
  };
}
